export const getComparableQuickOrderQuantityValue = (value) =>
  `${Number(value || 0)}`;

export const hasQuickOrderPendingQuantityEdits = (pendingQuantityEdits = {}) =>
  Object.values(pendingQuantityEdits).some((count) => count > 0);

export const getQuickOrderCurrentItem = ({ batchQueuesEnabled, item }) =>
  batchQueuesEnabled ? item : item.variants[0];

export const getQuickOrderSuccessQuantityValue = ({
  batchQueuesEnabled,
  displayedLocationsIds,
  editLocationsIds,
  payloadItem,
}) => {
  const currentItem = getQuickOrderCurrentItem({
    batchQueuesEnabled,
    item: payloadItem,
  });

  if (editLocationsIds.length === 1) {
    return getComparableQuickOrderQuantityValue(
      getLocationQuantity({
        currentItem,
        locationId: editLocationsIds[0],
      })
    );
  }

  return getComparableQuickOrderQuantityValue(
    getAllLocationsQuantity({
      currentItem,
      selectedLocations: displayedLocationsIds.map((locationId) => ({
        id: locationId,
      })),
    })
  );
};

export const getLocationQuantity = ({ currentItem, locationId }) =>
  currentItem.locations.find(
    (variantLocation) =>
      Number(variantLocation.dispensaryLocationId) === Number(locationId)
  )?.quantity || 0;

export const getAllLocationsQuantity = ({ currentItem, selectedLocations }) => {
  const selectedQuantities = selectedLocations.map((selectedLocation) =>
    getLocationQuantity({
      currentItem,
      locationId: selectedLocation.id,
    })
  );

  if (!selectedQuantities.length) {
    return 0;
  }

  const [firstQuantity] = selectedQuantities;

  return selectedQuantities.every(
    (quantity) => Number(quantity) === Number(firstQuantity)
  )
    ? firstQuantity
    : 0;
};

export const getSelectedLocationSummary = ({
  currentItem,
  selectedLocations,
}) => {
  const selectedLocationIds = selectedLocations.map((selectedLocation) =>
    Number(selectedLocation.id)
  );

  return currentItem.locations.reduce(
    (selectedLocationSummary, itemLocation) => {
      if (
        !selectedLocationIds.includes(Number(itemLocation.dispensaryLocationId))
      ) {
        return selectedLocationSummary;
      }

      return {
        selectedLocationCount:
          selectedLocationSummary.selectedLocationCount +
          Number(itemLocation.quantity || 0),
        selectedLocationTotal:
          selectedLocationSummary.selectedLocationTotal +
          Number(itemLocation.total ?? itemLocation.subTotal ?? 0),
      };
    },
    {
      selectedLocationCount: 0,
      selectedLocationTotal: 0,
    }
  );
};

export const getSelectedLocationTotal = ({
  batchQueuesEnabled,
  currentItem,
  item,
  selectedLocations,
  selectedLocationSummary,
  totalQty,
}) => {
  const apiSelectedLocationTotal = Number(item.selectedLocationTotal);
  const selectedLocationIds = selectedLocations.map((selectedLocation) =>
    Number(selectedLocation.id)
  );
  const allItemLocationsSelected = (currentItem.locations || []).every(
    (itemLocation) =>
      selectedLocationIds.includes(Number(itemLocation.dispensaryLocationId))
  );

  if (
    !batchQueuesEnabled &&
    item.selectedLocationTotal != null &&
    allItemLocationsSelected
  ) {
    return apiSelectedLocationTotal;
  }

  const shouldProrateBatchQueueTotal =
    batchQueuesEnabled &&
    selectedLocationSummary.selectedLocationTotal === 0 &&
    item.selectedLocationTotal != null &&
    apiSelectedLocationTotal > 0 &&
    totalQty > 0 &&
    selectedLocationSummary.selectedLocationCount > 0;

  return shouldProrateBatchQueueTotal
    ? apiSelectedLocationTotal *
        (selectedLocationSummary.selectedLocationCount / totalQty)
    : selectedLocationSummary.selectedLocationTotal;
};

export const buildQuickOrderItemAlertKey = ({ cartItemId, locationId }) =>
  `${Number(cartItemId)}-${Number(locationId)}`;

export const normalizeQuickOrderItemAlert = (itemAlert = {}) => ({
  ...itemAlert,
  cartItemId: itemAlert.cartItemId ?? itemAlert.cart_item_id,
  locationId: itemAlert.locationId ?? itemAlert.location_id,
  subtitle: itemAlert.subtitle ?? itemAlert.sub_message,
});

export const normalizeQuickOrderItemAlerts = (quickOrderItemAlerts = []) =>
  quickOrderItemAlerts.map(normalizeQuickOrderItemAlert);

export const buildQuickOrderItemAlertsByLocation = (quickOrderItemAlerts) =>
  normalizeQuickOrderItemAlerts(quickOrderItemAlerts).reduce(
    (alertsByLocation, itemAlert) => {
      alertsByLocation[
        buildQuickOrderItemAlertKey({
          cartItemId: itemAlert.cartItemId,
          locationId: itemAlert.locationId,
        })
      ] = itemAlert;

      return alertsByLocation;
    },
    {}
  );

export const getQuickOrderItemAlert = ({
  cartItemId,
  isAllLocations,
  locationId,
  quickOrderItemAlerts = [],
}) => {
  if (isAllLocations || !locationId) {
    return undefined;
  }

  return buildQuickOrderItemAlertsByLocation(quickOrderItemAlerts)[
    buildQuickOrderItemAlertKey({
      cartItemId,
      locationId,
    })
  ];
};
